import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/Button'
import { ModalShell } from '@/components/ui/ModalShell'
import { useModalStore } from '@/hooks/useModalStore'
import { useDiagramStore } from '@/stores'
import type { Column, DatabaseDialect } from '@/types'
import { genId } from '@/utils/id'
import { isPrimaryKey } from '@/utils/columnKeys'
import { normCol } from '@/utils/normalizers'
import { CheckIcon, DetailsIcon, TrashIcon } from '@/components/ui/icons'
import { useColumnConfirmDelete } from '@/hooks/useColumnConfirmDelete'
import { isValidSqlIdentifier, sanitizeSqlIdentifierInput } from '@/utils/sqlIdentifiers'
import { ColumnTypeInput } from './ColumnTypeInput'

interface DraftColumn extends Column {
  rowId: string
  descOpen: boolean
}

function buildDraft(column?: Partial<Column>): DraftColumn {
  const normalized = normCol(column ?? {})
  return { ...normalized, rowId: genId('def-col'), descOpen: false }
}

function normalizeType(value: string) {
  const type = value.trim().toUpperCase()
  if (type === 'BOOL') return 'BOOLEAN'
  return type
}

function isMeaningful(column: DraftColumn) {
  return Boolean(
    column.name.trim() ||
      column.type.trim() ||
      column.desc.trim() ||
      column.defaultValue?.trim() ||
      column.checkExpression?.trim() ||
      column.pk ||
      column.fk ||
      column.uq ||
      column.notNull ||
      !column.blank,
  )
}

function serializeColumns(columns: DraftColumn[]) {
  return columns
    .filter(isMeaningful)
    .map((column) =>
      normCol({
        key: column.key,
        pk: column.pk,
        fk: column.fk,
        uq: column.pk ? true : column.uq,
        name: column.name.trim(),
        type: normalizeType(column.type),
        notNull: column.notNull,
        blank: column.blank,
        desc: column.desc.trim(),
        defaultValue: column.defaultValue?.trim() ?? '',
        checkExpression: column.checkExpression?.trim() ?? '',
      }),
    )
}

function DefaultsRow({
  column,
  t,
  onChange,
  onRemove,
  dialect,
  isDeleteConfirming,
}: {
  column: DraftColumn
  t: (key: string, options?: Record<string, unknown>) => string
  onChange: <K extends keyof DraftColumn>(rowId: string, field: K, value: DraftColumn[K]) => void
  onRemove: (rowId: string) => void
  dialect: DatabaseDialect
  isDeleteConfirming: boolean
}) {
  return (
    <div className="rounded-xl border px-2 py-2" style={{ borderColor: 'var(--border)', background: 'var(--bg-card)' }}>
      <div className="grid items-center gap-1.5" style={{ gridTemplateColumns: 'minmax(0,1fr) minmax(0,188px) 20px 20px 20px 24px 24px 28px 28px' }}>
        <input
          type="text"
          value={column.name}
          onChange={(event) => onChange(column.rowId, 'name', sanitizeSqlIdentifierInput(event.target.value))}
          placeholder={t('prompts.columnNamePlaceholder')}
          className="morph-input min-w-0"
        />
        <ColumnTypeInput
          value={column.type}
          onChange={(value) => onChange(column.rowId, 'type', value)}
          dialect={dialect}
        />
        <label className="flex items-center justify-center" title={t('tableModal.pkTitle')}>
          <input
            type="checkbox"
            checked={!!column.pk}
            onChange={(event) => onChange(column.rowId, 'pk', event.target.checked)}
            className="morph-checkbox"
          />
        </label>
        <label className="flex items-center justify-center" title={t('tableModal.fkTitle')}>
          <input
            type="checkbox"
            checked={!!column.fk}
            onChange={(event) => onChange(column.rowId, 'fk', event.target.checked)}
            className="morph-checkbox"
          />
        </label>
        <label className="flex items-center justify-center" title={t('tableModal.ukTitle')}>
          <input
            type="checkbox"
            checked={!!column.uq}
            onChange={(event) => onChange(column.rowId, 'uq', event.target.checked)}
            className="morph-checkbox"
          />
        </label>
        <label className="flex items-center justify-center" title={t('prompts.notNullTitle')}>
          <input
            type="checkbox"
            checked={column.notNull}
            onChange={(event) => onChange(column.rowId, 'notNull', event.target.checked)}
            className="morph-checkbox"
          />
        </label>
        <label className="flex items-center justify-center" title={t('prompts.blankTitle')}>
          <input
            type="checkbox"
            checked={column.blank}
            onChange={(event) => onChange(column.rowId, 'blank', event.target.checked)}
            className="morph-checkbox"
          />
        </label>
        <button
          type="button"
          onClick={() => onChange(column.rowId, 'descOpen', !column.descOpen)}
          className="flex h-7 w-7 items-center justify-center rounded-full text-[11px] transition-colors"
          style={{
            color: column.desc || column.defaultValue || column.checkExpression || column.descOpen ? '#8B5CF6' : 'var(--text-dim)',
            background: column.descOpen ? 'rgba(139,92,246,.1)' : 'rgba(99,102,241,.04)',
          }}
          title={t('prompts.columnDescriptionTitle')}
        >
          <DetailsIcon />
        </button>
        <button
          type="button"
          onClick={() => onRemove(column.rowId)}
          className="flex h-7 w-7 items-center justify-center rounded-full text-[11px] transition-colors"
          style={{
            color: isDeleteConfirming ? 'var(--color-warning)' : 'var(--color-danger)',
            background: isDeleteConfirming ? 'var(--color-warning-bg)' : 'var(--color-danger-bg)',
          }}
          title={isDeleteConfirming ? t('prompts.removeColumnConfirm') : t('prompts.deleteFkColumn')}
        >
          {isDeleteConfirming ? <CheckIcon /> : <TrashIcon />}
        </button>
      </div>

      {column.descOpen ? (
        <div className="mt-2 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <label className="min-w-0 text-[9px] uppercase tracking-wide" style={{ color: 'var(--text-dim)' }}>
              <span className="mb-1 block">DEFAULT</span>
              <input
                type="text"
                value={column.defaultValue ?? ''}
                onChange={(event) => onChange(column.rowId, 'defaultValue', event.target.value)}
                placeholder={t('prompts.columnDefaultPlaceholder')}
                className="morph-input min-w-0 normal-case tracking-normal"
              />
            </label>
            <label className="min-w-0 text-[9px] uppercase tracking-wide" style={{ color: 'var(--text-dim)' }}>
              <span className="mb-1 block">CHECK</span>
              <input
                type="text"
                value={column.checkExpression ?? ''}
                onChange={(event) => onChange(column.rowId, 'checkExpression', event.target.value)}
                placeholder={t('prompts.columnCheckPlaceholder')}
                className="morph-input min-w-0 normal-case tracking-normal"
              />
            </label>
          </div>
          <textarea
            value={column.desc}
            onChange={(event) => onChange(column.rowId, 'desc', event.target.value)}
            placeholder={t('prompts.columnDescriptionPlaceholder')}
            className="morph-textarea min-h-[60px]"
          />
        </div>
      ) : null}
    </div>
  )
}

export function DefaultsModal() {
  const { t } = useTranslation()
  const open = useModalStore((state) => state.defaultsOpen)
  const close = useModalStore((state) => state.closeDefaults)
  const defaults = useDiagramStore((state) => state.defaults)
  const dialect = useDiagramStore((state) => state.dialect)
  const setDefaults = useDiagramStore((state) => state.setDefaults)
  const { isConfirming, requestDelete, cancelConfirmation } = useColumnConfirmDelete()

  const [prefix, setPrefix] = useState<DraftColumn[]>([])
  const [suffix, setSuffix] = useState<DraftColumn[]>([])
  const [errorMessage, setErrorMessage] = useState('')

  useEffect(() => {
    if (!open) return
    setPrefix(defaults.prefix.map(buildDraft))
    setSuffix(defaults.suffix.map(buildDraft))
    setErrorMessage('')
    cancelConfirmation()
  }, [cancelConfirmation, defaults.prefix, defaults.suffix, open])

  const pkCount = useMemo(
    () => [...prefix, ...suffix].filter((column) => isPrimaryKey(column)).length,
    [prefix, suffix],
  )

  const updateRows = (
    target: 'prefix' | 'suffix',
    updater: (current: DraftColumn[]) => DraftColumn[],
  ) => {
    if (target === 'prefix') {
      setPrefix(updater)
      return
    }
    setSuffix(updater)
  }

  const updateColumn = <K extends keyof DraftColumn>(
    target: 'prefix' | 'suffix',
    rowId: string,
    field: K,
    value: DraftColumn[K],
  ) => {
    const apply = (rows: DraftColumn[]) =>
      rows.map((row) => (row.rowId === rowId ? { ...row, [field]: value } : row))

    if (field === 'pk') {
      setPrefix((current) =>
        apply(current).map((row) => {
          if (row.rowId === rowId && target === 'prefix') {
            if (!value) return { ...row, pk: false }
            return { ...row, pk: true, fk: false, uq: true }
          }
          return value && row.pk ? { ...row, pk: false } : row
        }),
      )
      setSuffix((current) =>
        apply(current).map((row) => {
          if (row.rowId === rowId && target === 'suffix') {
            if (!value) return { ...row, pk: false }
            return { ...row, pk: true, fk: false, uq: true }
          }
          return value && row.pk ? { ...row, pk: false } : row
        }),
      )
      return
    }

    if (field === 'fk') {
      updateRows(target, (current) =>
        apply(current).map((row) => {
          if (row.rowId !== rowId) return row
          if (!value) return { ...row, fk: false }
          return { ...row, fk: true, pk: false }
        }),
      )
      return
    }

    if (field === 'uq') {
      updateRows(target, (current) =>
        apply(current).map((row) => {
          if (row.rowId !== rowId) return row
          if (row.pk) return { ...row, uq: true }
          return { ...row, uq: Boolean(value) }
        }),
      )
      return
    }

    updateRows(target, (current) => apply(current))
  }

  const save = () => {
    setErrorMessage('')
    const nextPrefix = serializeColumns(prefix)
    const nextSuffix = serializeColumns(suffix)

    const unnamed = [...nextPrefix, ...nextSuffix].find((column) => !column.name.trim())
    if (unnamed) {
      setErrorMessage(t('defaults.columnNameRequired'))
      return
    }

    const invalidName = [...nextPrefix, ...nextSuffix].find(
      (column) => !isValidSqlIdentifier(column.name),
    )
    if (invalidName) {
      setErrorMessage(t('defaults.invalidColumnName', { name: invalidName.name || '—' }))
      return
    }

    const missingType = [...nextPrefix, ...nextSuffix].find((column) => !column.type.trim())
    if (missingType) {
      setErrorMessage(t('defaults.columnTypeRequired', { name: missingType.name }))
      return
    }

    const nextPkCount = [...nextPrefix, ...nextSuffix].filter((column) => isPrimaryKey(column)).length
    if (nextPkCount > 1) {
      setErrorMessage(t('tableModal.singlePkError'))
      return
    }

    setDefaults({ prefix: nextPrefix, suffix: nextSuffix })
    close()
  }

  const removeColumn = (
    target: 'prefix' | 'suffix',
    rowId: string,
  ) => {
    requestDelete(rowId, () => {
      updateRows(target, (current) => current.filter((column) => column.rowId !== rowId))
    })
  }

  return (
    <ModalShell
      open={open}
      onClose={close}
      title={t('defaults.title')}
      widthClassName="max-w-[700px]"
      footer={(
        <>
          <Button variant="ghost" onClick={close}>{t('common.cancel')}</Button>
          <Button variant="primary" onClick={save}>{t('defaults.save')}</Button>
        </>
      )}
    >
      <div className="space-y-4">
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-[11px]" style={{ color: 'var(--color-warning)' }}>
            <span>{t('defaults.prefix')}</span>
            <span className="h-px flex-1" style={{ background: 'var(--border)' }} />
          </div>
          <div className="grid items-center gap-1.5 px-2 text-[9px] uppercase tracking-[0.3px] leading-none" style={{ gridTemplateColumns: 'minmax(0,1fr) minmax(0,188px) 20px 20px 20px 24px 24px 28px 28px', color: 'var(--text-dim)' }}>
            <span>{t('common.name')}</span>
            <span>{t('common.type')}</span>
            <span className="text-center">PK</span>
            <span className="text-center">FK</span>
            <span className="text-center">UQ</span>
            <span className="text-center">NN</span>
            <span className="text-center">BL</span>
            <span className="flex items-center justify-center" title={t('common.details')}>
              <DetailsIcon />
            </span>
            <span className="flex items-center justify-center" title={t('common.delete')}>
              <TrashIcon />
            </span>
          </div>
          <div className="space-y-3">
            {prefix.map((column) => (
              <DefaultsRow key={column.rowId} column={column} t={t} dialect={dialect} isDeleteConfirming={isConfirming(column.rowId)} onChange={(rowId, field, value) => updateColumn('prefix', rowId, field, value)} onRemove={(rowId) => removeColumn('prefix', rowId)} />
            ))}
          </div>
          <button
            type="button"
            onClick={() => setPrefix((current) => [...current, buildDraft({ blank: true })])}
            className="flex h-10 w-full items-center justify-center rounded-xl border border-dashed text-[11px] font-medium"
            style={{ borderColor: 'rgba(99,102,241,.28)', color: 'var(--accent)', background: 'rgba(99,102,241,.08)' }}
          >
            {t('tableModal.addColumn')}
          </button>
        </div>

        <div className="space-y-3">
          <div className="flex items-center gap-2 text-[11px]" style={{ color: 'var(--color-success)' }}>
            <span>{t('defaults.suffix')}</span>
            <span className="h-px flex-1" style={{ background: 'var(--border)' }} />
          </div>
          <div className="grid items-center gap-1.5 px-2 text-[9px] uppercase tracking-[0.3px] leading-none" style={{ gridTemplateColumns: 'minmax(0,1fr) minmax(0,188px) 20px 20px 20px 24px 24px 28px 28px', color: 'var(--text-dim)' }}>
            <span>{t('common.name')}</span>
            <span>{t('common.type')}</span>
            <span className="text-center">PK</span>
            <span className="text-center">FK</span>
            <span className="text-center">UQ</span>
            <span className="text-center">NN</span>
            <span className="text-center">BL</span>
            <span className="flex items-center justify-center" title={t('common.details')}>
              <DetailsIcon />
            </span>
            <span className="flex items-center justify-center" title={t('common.delete')}>
              <TrashIcon />
            </span>
          </div>
          <div className="space-y-3">
            {suffix.map((column) => (
              <DefaultsRow key={column.rowId} column={column} t={t} dialect={dialect} isDeleteConfirming={isConfirming(column.rowId)} onChange={(rowId, field, value) => updateColumn('suffix', rowId, field, value)} onRemove={(rowId) => removeColumn('suffix', rowId)} />
            ))}
          </div>
          <button
            type="button"
            onClick={() => setSuffix((current) => [...current, buildDraft({ blank: true })])}
            className="flex h-10 w-full items-center justify-center rounded-xl border border-dashed text-[11px] font-medium"
            style={{ borderColor: 'rgba(99,102,241,.28)', color: 'var(--accent)', background: 'rgba(99,102,241,.08)' }}
          >
            {t('tableModal.addColumn')}
          </button>
        </div>
      </div>

      {errorMessage ? (
        <div className="mt-4 rounded-xl border px-3 py-2 text-[11px]" style={{ borderColor: 'var(--color-danger-border)', background: 'var(--color-danger-bg)', color: 'var(--color-danger)' }}>
          {errorMessage}
        </div>
      ) : null}

      <div className="mt-4 rounded-xl border px-3 py-2 text-[11px]" style={{ borderColor: 'var(--border)', color: 'var(--text-dim)' }}>
        {t('defaults.summary', { count: pkCount })}
      </div>
    </ModalShell>
  )
}
